import { useCallback, useState } from 'react';
import { useDmQuickstarts } from '../../../../../hooks/useDmQuickstarts';
import {
    pauseMonitoring,
    resumeMonitoring,
    startMonitoring,
    stopMonitoring
} from '../../../../../services/dmMonitoring';
import type { ActionMode, ActionSteps } from './actionSteps';
import { AlignerChangeStep } from './AlignerChangeStep';
import type { DmBaseline } from './dmControlDraft';
import { errorText, ymdInDays } from './dmControlUtils';
import { IdleActions } from './IdleActions';
import { RequestScanStep } from './RequestScanStep';
import { StackedLayers } from './StackedLayers';
import { PauseConfirm, StartConfirm, StopConfirm } from './StatusConfirmSteps';
import { useAlignerChangeRequest } from './useAlignerChangeRequest';
import { useDmControlMutation } from './useDmControlMutation';

const DEFAULT_PAUSE_DAYS = 7;

/** Midday UTC keeps the chosen day whatever the patient's timezone. */
function toUnixMidday(ymd: string): number {
    return Math.floor(new Date(`${ymd}T12:00:00Z`).getTime() / 1000);
}

interface MonitoringActionsProps {
    patientId: string;
    /** Named in the stop warning; falls back to "this patient". */
    patientName?: string;
    base: DmBaseline;
    /** DM will tell the patient to change aligners at once. */
    alignerChangePending: boolean;
    /** After an action went through, e.g. to confirm it next to the status. */
    onDone?: () => void;
    steps: ActionSteps;
}

// The left of the monitoring panel. Every action applies to DM at once, behind its own confirm
// step; all steps stay mounted so the column keeps one height (see StackedLayers).
export function MonitoringActions({
    patientId,
    patientName,
    base,
    alignerChangePending,
    onDone,
    steps
}: MonitoringActionsProps) {
    const { mode, setMode, scanMessage, setScanMessage } = steps;
    const backToIdle = useCallback(() => setMode('idle'), [setMode]);
    // Remounts the scan step on each open, so its last error starts empty.
    const [scanOpenCount, setScanOpenCount] = useState(0);
    const [resumeOn, setResumeOn] = useState(base.resumeOn ?? ymdInDays(DEFAULT_PAUSE_DAYS));
    const [quickstartId, setQuickstartId] = useState<string | null>(null);
    // A stopped monitoring starts again like a new one: DM needs a quickstart picked again.
    const canStart = base.status === 'not_started' || base.status === 'stopped';
    const quickstarts = useDmQuickstarts(canStart ? patientId : undefined);
    const chosenQuickstart = quickstartId ?? quickstarts.data[0]?.id ?? null;
    const finish = () => {
        setMode('idle');
        onDone?.();
    };
    const action = useDmControlMutation(patientId, (run: () => Promise<unknown>) => run(), finish);
    const aligner = useAlignerChangeRequest(patientId, {
        onDone: finish,
        onConflict: backToIdle
    });
    const busy = action.saving || aligner.saving;
    const statusErrorIn = (step: ActionMode) => (mode === step ? action.error : null);
    const alignerErrorIn = (step: ActionMode) => (mode === step ? aligner.error : null);
    const step = { busy, onCancel: backToIdle };
    const paused = base.status === 'paused';
    const openAlignerStep = () => {
        aligner.reset();
        setMode(alignerChangePending ? 'cancelAligner' : 'forceAligner');
    };
    const alignerStep = (cancelling: boolean) => (
        <AlignerChangeStep
            patientName={patientName}
            pending={cancelling}
            busy={aligner.saving}
            error={alignerErrorIn(cancelling ? 'cancelAligner' : 'forceAligner')}
            onConfirm={() => aligner.run(cancelling)}
            onCancel={backToIdle}
        />
    );

    return (
        <StackedLayers
            active={mode}
            layers={[
                {
                    key: 'idle',
                    content: (
                        <IdleActions
                            status={base.status}
                            alignerChangePending={alignerChangePending}
                            busy={action.saving}
                            error={alignerErrorIn('idle') ?? statusErrorIn('idle')}
                            onStart={() => setMode('start')}
                            onPause={() => setMode('pause')}
                            onScheduleResume={() => {
                                setResumeOn(base.resumeOn ?? ymdInDays(DEFAULT_PAUSE_DAYS));
                                setMode('pause');
                            }}
                            onResumeNow={() => action.run(() => resumeMonitoring(patientId))}
                            onStop={() => setMode('stop')}
                            onRequestScan={() => {
                                setScanOpenCount((count) => count + 1);
                                setScanMessage('');
                                setMode('scan');
                            }}
                            onAlignerChange={base.tracksAligners ? openAlignerStep : undefined}
                        />
                    )
                },
                {
                    key: 'start',
                    content: (
                        <StartConfirm
                            {...step}
                            error={statusErrorIn('start')}
                            quickstarts={quickstarts.data}
                            loading={quickstarts.isLoading}
                            loadError={quickstarts.error ? errorText(quickstarts.error) : null}
                            selectedId={chosenQuickstart}
                            onSelect={setQuickstartId}
                            onConfirm={() =>
                                chosenQuickstart &&
                                action.run(() =>
                                    startMonitoring(patientId, { quickstartId: chosenQuickstart })
                                )
                            }
                        />
                    )
                },
                {
                    key: 'pause',
                    content: (
                        <PauseConfirm
                            {...step}
                            error={statusErrorIn('pause')}
                            paused={paused}
                            resumeOn={resumeOn}
                            savedResumeOn={base.resumeOn}
                            onResumeOnChange={setResumeOn}
                            // DM refuses to pause a paused patient, so a new date goes through resume, as in DM.
                            onConfirm={() =>
                                action.run(() =>
                                    paused
                                        ? resumeMonitoring(patientId, toUnixMidday(resumeOn))
                                        : pauseMonitoring(patientId, {
                                              resumeDate: toUnixMidday(resumeOn)
                                          })
                                )
                            }
                        />
                    )
                },
                {
                    key: 'stop',
                    content: (
                        <StopConfirm
                            {...step}
                            error={statusErrorIn('stop')}
                            patientName={patientName}
                            onConfirm={() => action.run(() => stopMonitoring(patientId))}
                        />
                    )
                },
                {
                    key: 'scan',
                    content: (
                        <RequestScanStep
                            key={scanOpenCount}
                            patientId={patientId}
                            patientName={patientName}
                            message={scanMessage}
                            onMessageChange={setScanMessage}
                            onCancel={backToIdle}
                            onDone={backToIdle}
                        />
                    )
                },
                { key: 'forceAligner', content: alignerStep(false) },
                { key: 'cancelAligner', content: alignerStep(true) }
            ]}
        />
    );
}
